/**
 * IntegrationsGrid — the original Integrations Hub body, extracted so it can be
 * hosted inside the Control Center's "Integrations" tab (which lives at
 * `/integrations`).
 *
 * This is the NICE, well-designed integrations UI: the `MCP` logo-card grid
 * (real platform logos, status pills, connect/disconnect/configure popups) with
 * the shared search + status `FilterBox` on top, plus the "Request new
 * integration" popup. It replaces the plain `PlatformConnections` grid that was
 * mistakenly used here.
 *
 * IMPORTANT: the `MCP` component completes OAuth by redirecting back to
 * `/integrations` and reading the `?code`/`?state` off the URL on mount — which
 * is exactly why the host page now lives at `/integrations`.
 *
 * RBAC: owner / team_lead / manager (`integrations.manage`) can connect/edit;
 * analyst / read_only browse read-only (the cards hide their write affordances).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MCP from './MCP/MCP';
import RequestMCP from './MCP/RequestMCP';
import Popup from '../../ui/popup/Popup';
import FilterBox from '../../ui/boxes/FilterBox';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { plusSignSVG } from '../../lib/allCustomSVGs';
import get_all_available_mcp_api_call from '../../lib/api/mcp/get-all-available-mcp';
import { useCan } from '../../lib/rbac';
import {
  MCP_STATUS_FILTER_ALL,
  MCP_STATUS_FILTER_OPTIONS,
  getMcpStatusFilterLabelKey,
} from './MCP/mcp-connection-status';

const IntegrationsGrid = ({
  onPlatformsLoaded,
}: {
  // The platform rows, handed up on every (re)load of the grid.
  onPlatformsLoaded?: (rows: any[]) => void;
}) => {
  const { t } = useTranslation();

  // W1 — connecting/requesting integrations is owner/team_lead/manager only.
  const canManageIntegrations = useCan('integrations.manage');

  const [isRequestMCPPopupOpen, setIsRequestMCPPopupOpen] = useState(false);
  const [scheduledMCPs, setScheduledMCPs] = useState<any>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(MCP_STATUS_FILTER_ALL);
  const [loading, setLoading] = useState('pending');

  const toggleRequestMCPPopupModal = () =>
    setIsRequestMCPPopupOpen((open) => !open);

  const getAllPendingMCP = async () => {
    setLoading('pending');
    const res = await get_all_available_mcp_api_call(true);
    if (res && res?.length > 0) {
      setLoading('success');
      setScheduledMCPs(res);
    } else {
      setLoading('null');
      setScheduledMCPs([]);
    }
  };

  useEffect(() => {
    getAllPendingMCP();
  }, []);

  return (
    <div className='mcp_page'>
      {canManageIntegrations && (
        <div
          className='ccIntegrations__toolbar'
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '1.2rem',
          }}
        >
          <MainSquareButton
            value={t('RequestNewIntegration')}
            type='button'
            buttonType='white'
            normal
            icon={plusSignSVG}
            onClick={toggleRequestMCPPopupModal}
          />
        </div>
      )}

      {/* The options are owned by `mcp-connection-status`: one per state the
          status helper can return, so every option selects the same set the
          pills show and none of them can be empty by construction. */}
      <FilterBox
        filterOptions={MCP_STATUS_FILTER_OPTIONS}
        getOptionLabel={(option) => t(getMcpStatusFilterLabelKey(option))}
        extraClassName='mcpIntegration-filterBox--identifier'
        initialFilter={MCP_STATUS_FILTER_ALL}
        placeholder={t('IntegrationsSearchPlaceholder')}
        onSearchChange={(value) => setSearchValue(value)}
        onFilterChange={(filter) => setSelectedFilter(filter)}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          marginBottom: '2rem',
        }}
      >
        <MCP
          searchValue={searchValue}
          selectedFilter={selectedFilter}
          canManage={canManageIntegrations}
          onListLoaded={onPlatformsLoaded}
        />
      </div>

      {/* Request new integration popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={toggleRequestMCPPopupModal}
        trigger={isRequestMCPPopupOpen}
      >
        <RequestMCP
          scheduledMCPs={scheduledMCPs}
          loading={loading}
          toggleRequestMCPPopupModal={toggleRequestMCPPopupModal}
        />
      </Popup>
    </div>
  );
};

export default IntegrationsGrid;
